/**
 * Tasks page data layer (Plan 05).
 *
 * Reads + one write:
 *   - `useMyTasksQuery({ subKind?, overdue? })` — current user's open tasks
 *   - `useTaskQuery(id)` — one task by id, backing the /tasks/:id detail
 *      page + the notification deep-link
 *   - `useTaskCountsQuery()` — counts grouped by subKind + sentinels
 *      (polls every 30s to catch cross-tab dismissals)
 *   - `useDismissBlockingTaskMutation()` — operator override with a required reason
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TaskCategory =
  | 'COMPLIANCE'
  | 'LEGAL'
  | 'ONBOARDING'
  | 'DATA_QUALITY'
  | 'FINANCIAL'
  | 'OPERATIONS'
  | 'PAYMENT_BLOCKING'

export interface MyTaskRow {
  id: string
  organizationId: string
  category: TaskCategory
  subKind: string | null
  title: string
  description: string | null
  blocksAction: string
  blocksSubjectKind: string
  blocksSubjectId: string | null
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'DISMISSED'
  dueAt: string | null
  createdAt: string
  assignedToProfileCode: string | null
  taskerTaskId: string | null
  /** Non-archived comment count. Present for onboarding rows; undefined otherwise. */
  commentCount?: number
  organization: {
    id: string
    name: string
  }
  /**
   * Shipment metadata the tasks board groups on (rollup tickets: one per
   * day / mealtime / drop). Present only on shipment-anchored rows of the
   * rollup subKinds (temp log / confirm delivery / complete service).
   */
  shipment?: TaskShipmentMeta | null
}

/** Rollup metadata for shipment-anchored task rows. Dates are ISO yyyy-mm-dd. */
export interface TaskShipmentMeta {
  id: string
  schoolLocationId: string
  schoolLocationName: string
  serviceDate: string
  deliveryDate: string
  mealtime: string
}

export interface MyTasksResponse {
  tasks: MyTaskRow[]
}

export interface MyTasksVariables {
  subKind?: string | null
  overdue?: boolean | null
}

export interface TaskCount {
  subKind: string
  count: number
}

export interface TaskCountsResponse {
  counts: TaskCount[]
}

export interface DismissBlockingTaskVariables {
  id: string
  reason: string
}

export interface DismissBlockingTaskResponse {
  id: string
  status: 'DISMISSED'
  dismissReason: string | null
  dismissedByUserId: string | null
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMyTasks(vars: MyTasksVariables = {}, signal?: AbortSignal): Promise<MyTasksResponse> {
  return apiFetch<MyTasksResponse>('/api/tasks', {
    query: {
      subKind: vars.subKind ?? undefined,
      overdue: vars.overdue ?? undefined,
    },
    signal,
  })
}

/**
 * Fetch a single task by id — the data source for the /tasks/:id detail
 * page (reached from a task notification's deep-link). Unwraps the
 * `{ task }` envelope the endpoint returns. Throws `ApiError` (404 for
 * unknown / not-entitled ids) so the page can branch on the status.
 */
export async function fetchTask(id: string, signal?: AbortSignal): Promise<MyTaskRow> {
  const { task } = await apiFetch<{ task: MyTaskRow }>(`/api/tasks/${encodeURIComponent(id)}`, { signal })
  return task
}

export async function fetchTaskCounts(signal?: AbortSignal): Promise<TaskCountsResponse> {
  return apiFetch<TaskCountsResponse>('/api/tasks/counts', { signal })
}

export async function dismissBlockingTask(vars: DismissBlockingTaskVariables): Promise<DismissBlockingTaskResponse> {
  return apiFetch<DismissBlockingTaskResponse>(`/api/tasks/${encodeURIComponent(vars.id)}/dismiss`, {
    method: 'POST',
    body: { reason: vars.reason },
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMyTasksQuery(vars: MyTasksVariables = {}) {
  return useQuery<MyTasksResponse>({
    queryKey: ['tasks', { subKind: vars.subKind ?? null, overdue: vars.overdue ?? null }],
    queryFn: ({ signal }) => fetchMyTasks(vars, signal),
    // Match the chip-counts poll so the list view catches cross-tab
    // dismissals. Cheap query — keyed on assignedToUserId.
    refetchInterval: 30_000,
  })
}

/**
 * Fetch one task for the detail page. Keyed under the `['tasks']`
 * prefix so the dismiss/completion mutations' `invalidateQueries(['tasks'])`
 * also refresh an open detail view. A 404 (unknown / not-entitled id)
 * fails fast — no retries — so the page shows the friendly not-found
 * state promptly.
 */
export function useTaskQuery(id: string | null) {
  return useQuery<MyTaskRow, ApiError>({
    queryKey: ['tasks', 'detail', id],
    queryFn: ({ signal }) => fetchTask(id as string, signal),
    enabled: Boolean(id),
    retry: (_count, err) => !(err instanceof ApiError && err.status === 404),
  })
}

export function useTaskCountsQuery() {
  return useQuery<TaskCountsResponse>({
    queryKey: ['tasks', 'counts'],
    queryFn: ({ signal }) => fetchTaskCounts(signal),
    refetchInterval: 30_000,
  })
}

export function useDismissBlockingTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: dismissBlockingTask,
    onSuccess: () => {
      // Refetch both the list + the chip counts so the row disappears
      // and the badges decrement in one round-trip.
      void qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Pure helpers (kept compatible with the previous query module)
// ---------------------------------------------------------------------------

export type TaskFilter = 'all' | 'overdue' | string

/**
 * Pure helper: given the chip strip selection, return the query
 * variables to pass to `useMyTasksQuery`. Exported so the page + the
 * filter strip + tests can share the same logic.
 */
export function variablesForFilter(filter: TaskFilter): MyTasksVariables {
  if (filter === 'all') {
    return {}
  }
  if (filter === 'overdue') {
    return { overdue: true }
  }
  return { subKind: filter }
}

/**
 * Index a `TaskCount[]` row list by subKind for O(1) lookups in
 * components rendering the chip badges. Sentinel keys (`overdue`,
 * `__all`) live in the same map.
 */
export function indexTaskCounts(rows: ReadonlyArray<TaskCount>): Record<string, number> {
  const map: Record<string, number> = {}
  for (const row of rows) {
    map[row.subKind] = row.count
  }
  return map
}
